import {
  Box, Button, Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import {
  ApiError, NotFoundErrorCode, UnauthorizedErrorCodes, GoneErrorCode,
} from '../../utils/errors';
import { ThirdPartyType } from '../../types';
import { PublicPageLayout } from '../page-shell/public-page-layout';

const CREDIT_PULSE_URL = 'https://creditpulse.com';

interface ErrorPageProps {
  errorType: ApiError;
  link?: string;
  thirdPartyType?: ThirdPartyType;
}

const UNAUTHORIZED_MESSAGE_KEY: Record<ThirdPartyType, string> = {
  [ThirdPartyType.TRADE_REFERENCE]: 'additional_trade_references_unauthorized_message',
  [ThirdPartyType.PERSONAL_GUARANTOR]: 'additional_personal_guarantors_unauthorized_message',
};

const getErrorPageMessages = (errorType: ApiError, thirdPartyType: ThirdPartyType) => {
  switch (errorType.code) {
    case NotFoundErrorCode.SettingsNotFound:
      return {
        headerTranslationKey: 'application_not_found_message',
        buttonTranslationKey: 'go_to_credit_pulse',
      };
    case NotFoundErrorCode.SubmissionNotFound:
      return {
        headerTranslationKey: 'submission_not_found_message',
        buttonTranslationKey: 'return_to_application',
      };
    case NotFoundErrorCode.ThirdPartyNotFound:
      return {
        headerTranslationKey: 'generic_not_found_message',
        buttonTranslationKey: 'go_to_credit_pulse',
      };
    case GoneErrorCode.TradeReferenceNoLongerRequired:
      return {
        headerTranslationKey: 'trade_reference_no_longer_required_message',
        buttonTranslationKey: 'go_to_credit_pulse',
      };
    case GoneErrorCode.PersonalGuarantorNoLongerRequired:
      return {
        headerTranslationKey: 'personal_guarantor_no_longer_required_message',
        buttonTranslationKey: 'go_to_credit_pulse',
      };
    case NotFoundErrorCode.AdditionalThirdPartiesNotFound:
      return {
        headerTranslationKey: 'additional_third_parties_not_found_message',
        buttonTranslationKey: 'return_to_application',
      };
    case UnauthorizedErrorCodes.AdditionalThirdPartiesUnauthorized:
      return {
        headerTranslationKey: UNAUTHORIZED_MESSAGE_KEY[thirdPartyType],
        buttonTranslationKey: 'return_to_application',
      };
    default:
      if (errorType.status === 401) {
        return {
          headerTranslationKey: 'generic_not_found_message',
          buttonTranslationKey: 'return_to_application',
        };
      }
      return {
        headerTranslationKey: 'generic_not_found_message',
        buttonTranslationKey: 'go_to_credit_pulse',
      };
  }
};

const ErrorPage = ({
  errorType,
  link = undefined,
  thirdPartyType = ThirdPartyType.TRADE_REFERENCE,
}: ErrorPageProps) => {
  const { t } = useTranslation('components', { keyPrefix: 'status-pages' });

  const { headerTranslationKey, buttonTranslationKey } = getErrorPageMessages(errorType, thirdPartyType);

  const buttonUrl = buttonTranslationKey === 'go_to_credit_pulse' ? CREDIT_PULSE_URL : link;

  return (
    <PublicPageLayout>
      <Box
        sx={{
          px: 5,
          py: 6,
          textAlign: 'center',
        }}
      >
        <Typography
          gutterBottom
          fontWeight={700}
          variant='h5'
        >
          {t(headerTranslationKey)}
        </Typography>
        <Typography
          color='text.secondary'
          variant='body1'
        >
          {t('email_link_warning')}
        </Typography>
        {buttonUrl && (
          <Button
            component={buttonUrl.startsWith('http') ? 'a' : Link}
            href={buttonUrl.startsWith('http') ? buttonUrl : undefined}
            sx={{ mt: 2 }}
            to={buttonUrl.startsWith('http') ? undefined : buttonUrl}
            variant='contained'
          >
            {t(buttonTranslationKey)}
          </Button>
        )}
      </Box>
    </PublicPageLayout>
  );
};

export default ErrorPage;
